import React, { useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useFormContext } from "react-hook-form";
import {
  MAutocomplete,
  MDatePicker,
  MTextField,
  ResponsiveFormContainer,
} from "@app/components";
import { useInstallation, useUser } from "@services/servicesGenericsTables";
import dayjs from "dayjs";
import {
  historyDosesConfig,
  processHistoryDoses,
  stdevS,
} from "../utils/helpers";
import { useTransitDose } from "../utils/useTransitDose";
import { mapArrayToNumber } from "@app/utils/functions";

const TransitDosesForm = () => {
  const { installationList } = useInstallation();
  const { userList } = useUser();

  const { fetchByInstalacion, preloadTransitDoses } = useTransitDose();

  const form = useFormContext();

  const averageDeleteDate = form.watch("average_delete_date");
  const averageReadDate = form.watch("average_read_date");
  const crystal_1_transit_1 = form.watch("crystal_1_transit_1");
  const crystal_2_transit_1 = form.watch("crystal_2_transit_1");
  const crystal_3_transit_1 = form.watch("crystal_3_transit_1");
  const crystal_4_transit_1 = form.watch("crystal_4_transit_1");
  const crystal_1_transit_2 = form.watch("crystal_1_transit_2");
  const crystal_2_transit_2 = form.watch("crystal_2_transit_2");
  const crystal_3_transit_2 = form.watch("crystal_3_transit_2");
  const crystal_4_transit_2 = form.watch("crystal_4_transit_2");
  const transit_days = form.watch("transit_days");
  const instalation = form.watch("instalation");
  const timelyDoseTrafficCurrent = form.watch("timely_dose_of_traffic_current");

  useEffect(() => {
    const fields = [
      crystal_1_transit_1,
      crystal_2_transit_1,
      crystal_3_transit_1,
      crystal_4_transit_1,
      crystal_1_transit_2,
      crystal_2_transit_2,
      crystal_3_transit_2,
      crystal_4_transit_2,
    ];

    const numericFields = mapArrayToNumber(fields);

    const sumTimelyDoseOfTraffic = numericFields.reduce(
      (acc, value) => acc + value,
      0
    );

    const countFieldWithValue = numericFields.filter(
      (value) => value > 0
    ).length;

    const numericTransitDays = parseFloat(transit_days) || 1;

    const calculateTimelyDoseOfTraffic =
      sumTimelyDoseOfTraffic / countFieldWithValue / numericTransitDays;

    form.setValue(
      "timely_dose_of_traffic",
      calculateTimelyDoseOfTraffic
        ? calculateTimelyDoseOfTraffic.toFixed(2)
        : undefined
    );

    form.setValue(
      "timely_dose_of_traffic_current",
      calculateTimelyDoseOfTraffic
        ? calculateTimelyDoseOfTraffic.toFixed(2)
        : undefined
    );

    const n = fields.filter(
      (row) => row !== "" && row !== null && row !== undefined
    ).length;

    if (n > 1) {
      const convertFields = mapArrayToNumber(fields);
      const sampleStandardDeviation = stdevS(convertFields) / transit_days;

      form.setValue(
        "error_in_transit_dose",
        !!sampleStandardDeviation
          ? sampleStandardDeviation.toFixed(2)
          : undefined
      );
    } else {
      form.setValue("error_in_transit_dose", undefined);
    }
  }, [
    crystal_1_transit_1,
    crystal_2_transit_1,
    crystal_3_transit_1,
    crystal_4_transit_1,
    crystal_1_transit_2,
    crystal_2_transit_2,
    crystal_3_transit_2,
    crystal_4_transit_2,
    transit_days,
  ]);

  useEffect(() => {
    if (averageDeleteDate && averageReadDate) {
      const deleteDate = dayjs(averageDeleteDate);
      const readDate = dayjs(averageReadDate);

      const daysDifference = readDate.diff(deleteDate, "day", true);
      form.setValue("transit_days", daysDifference.toFixed(3));
    }
  }, [averageDeleteDate, averageReadDate]);

  useEffect(() => {
    if (instalation) {
      fetchByInstalacion(instalation.id, form.getValues("id"));
    }
  }, [instalation]);

  useEffect(() => {
    if (preloadTransitDoses.length > 0) {
      form.setValue(
        "timely_dose_of_traffic_previous",
        preloadTransitDoses[0]
          ? preloadTransitDoses[0].timely_dose_of_traffic
          : undefined
      );
      form.setValue(
        "timely_dose_of_traffic_previous_2",
        preloadTransitDoses[1]
          ? preloadTransitDoses[1].timely_dose_of_traffic
          : undefined
      );
      form.setValue(
        "timely_dose_of_traffic_previous_3",
        preloadTransitDoses[2]
          ? preloadTransitDoses[2].timely_dose_of_traffic
          : undefined
      );
    } else {
      form.setValue("timely_dose_of_traffic_previous", undefined);
      form.setValue("timely_dose_of_traffic_previous_2", undefined);
      form.setValue("timely_dose_of_traffic_previous_3", undefined);
    }
  }, [instalation, preloadTransitDoses]);

  useEffect(() => {
    if (preloadTransitDoses.length > 0) {
      const { average, calculatedStdev } = processHistoryDoses(
        preloadTransitDoses,
        form.watch("timely_dose_of_traffic") || 0
      );

      form.setValue(
        "history_average_transit_dose",
        average ? average.toFixed(2) : 0
      );

      form.setValue(
        "history_error_in_transit_dose",
        calculatedStdev ? calculatedStdev.toFixed(2) : 0
      );
    }
  }, [timelyDoseTrafficCurrent]);

  return (
    <>
      <Box mt={2}>
        <ResponsiveFormContainer columns={3}>
          <MAutocomplete
            name="analyst"
            options={userList}
            label="Seleccione un analista"
            getOptionLabel={(option) => option.username || ""}
            validation={{ required: "El analista es requerido" }}
          />
          <MDatePicker
            name="registration_date"
            pickerType="date"
            label="Fecha de registro"
            validation={{
              required: "La fecha es requerida",
            }}
          />
          <MAutocomplete
            name="instalation"
            options={installationList}
            label="Instalación"
            getOptionLabel={(option) =>
              `${option.codigo} - ${option.descripcion}`
            }
            validation={{ required: "La instalación es requerida" }}
          />
          <MDatePicker
            name="average_delete_date"
            pickerType="datetime"
            label="Fecha y hora media de borrado"
            validation={{
              required: "La fecha es requerida",
            }}
          />
          <MDatePicker
            name="average_read_date"
            pickerType="datetime"
            label="Fecha y hora media de lectura"
            validation={{
              required: "La fecha es requerida",
            }}
          />
          <MTextField
            name="transit_days"
            label="Días de tránsito"
            placeholder="Días de tránsito"
            fullWidth
            disabled
          />
        </ResponsiveFormContainer>
      </Box>
      <Box sx={{ mt: 4, paddingLeft: 5, paddingTop: 4 }}>
        <Grid
          container
          spacing={5}
          pb={4}
          pr={4}
          sx={{ border: "1px solid #e0e0e0" }}
        >
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Lecturas de tránsito (µSv)
            </Typography>
            <Grid container spacing={2}>
              {/* Tránsito 1 */}
              <Grid item xs={6} mt={1.5}>
                <Typography variant="subtitle2" gutterBottom>
                  TRÁNSITO 1
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {[1, 2, 3, 4].map((cristal) => (
                    <MTextField
                      key={`crystal_${cristal}_transit_1`}
                      name={`crystal_${cristal}_transit_1`}
                      label={`Cristal ${cristal}`}
                      placeholder="Días de tránsito"
                      fullWidth
                      type="number"
                    />
                  ))}
                </Box>
              </Grid>

              <Grid item xs={6} mt={1.5}>
                <Typography variant="subtitle2" gutterBottom>
                  TRÁNSITO 2
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {[1, 2, 3, 4].map((cristal) => (
                    <MTextField
                      key={`crystal_${cristal}_transit_2`}
                      name={`crystal_${cristal}_transit_2`}
                      label={`Cristal ${cristal}`}
                      placeholder="Días de tránsito"
                      fullWidth
                      type="number"
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Últimas dosis puntuales de tránsito (µSv/día)
            </Typography>

            <Box mt={2} sx={{ height: "24px" }} />
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="flex-end"
            >
              {historyDosesConfig.map((row, index) => (
                <Grid item xs={12} key={`dose-${index}`}>
                  <MTextField
                    key={`dose-${form.watch(row.value)}`}
                    name={row.value}
                    label={row.label}
                    placeholder="µSv/día"
                    fullWidth
                    disabled
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      <ResponsiveFormContainer columns={2} mt={2}>
        <MTextField
          name="timely_dose_of_traffic"
          label="Dosis puntual de tránsito (µSv/día)"
          placeholder="Dosis puntual de tránsito (µSv/día)"
          fullWidth
          disabled
        />
        <MTextField
          name="history_average_transit_dose"
          label="Dosis media de tránsito (µSv/día)"
          placeholder="Dosis media de tránsito (µSv/día)"
          fullWidth
          disabled
        />
        <MTextField
          name="error_in_transit_dose"
          label="Error dosis puntual de tránsito (µSv/día)"
          placeholder="Error dosis puntual de tránsito (µSv/día)"
          fullWidth
          disabled
        />
        <MTextField
          name="history_error_in_transit_dose"
          label="Error dosis media (µSv/día)"
          placeholder="Error dosis media (µSv/día)"
          fullWidth
          disabled
        />
      </ResponsiveFormContainer>
    </>
  );
};

export default TransitDosesForm;
